// Вкладка «Реквизиты». Куда переводить зарплату этому человеку и что он подписывал раньше.
//
// Полного номера карты здесь нет и не будет: сервер отдаёт четыре последние цифры, и это весь
// объём данных на этой стороне. Сверить карту глазами по четырём цифрам можно, восстановить
// номер — нет.
//
// Заявление не правится, а заменяется: кнопка называется «Новое заявление», а не «Изменить».
// Реквизиты — подписанный документ, и правка задним числом делает невозможным ответ на вопрос
// «куда мы переводили в мае».

import { RiAddLine, RiBankCardLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import type { ApplicationOut } from '@/shared/api/endpoints/payouts';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { AttachmentImage } from '@/shared/ui/attachment';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { usePayoutApplicationsQuery, usePayoutFactsQuery } from '../queries';
import { БланкЗаявления } from './PayoutApplicationBlank';
import type { PeopleRoute } from '../route';
import { openPanelHref, PANEL_PAYOUT } from '../route';

const EDIT_PERMISSION = 'core.payout.edit';

const СПОСОБЫ: Readonly<Record<string, string>> = {
  card: 'На карту',
  sbp: 'По номеру телефона',
};

/** Куда именно переводят: карта, телефон или счёт. Пусто быть не может — это проверяет сервер. */
function реквизит(заявление: ApplicationOut): string {
  if (заявление.card_last4) {
    return `карта ···· ${заявление.card_last4}`;
  }
  if (заявление.phone) {
    return `телефон ···· ${заявление.phone.slice(-4)}`;
  }
  if (заявление.settlement_account) {
    return `счёт ···· ${заявление.settlement_account.slice(-4)}`;
  }
  return '—';
}

export function PayoutsTab({ employee, route }: { employee: EmployeeOut; route: PeopleRoute }) {
  const navigate = useNavigate();
  const query = usePayoutApplicationsQuery(employee.id);
  const permissions = useAuthStore((state) => state.permissions);
  const canEdit = can(permissions, EDIT_PERMISSION);

  // Без права кнопки НЕТ, а вместо неё написано, кто это делает.
  //
  // Раньше она стояла серой и молчала: выключенная кнопка не доводит нажатие до обработчика,
  // человек жмёт и не получает ничего — ни отклика, ни подсказки. Со стороны это неотличимо
  // от сломанной программы (`.claude/rules/ui-views.md`, «гейт без объяснения — дефект»).
  // В пустом состоянии этой вкладки так и было сделано с самого начала; здесь — доделано.
  const кнопка = canEdit ? (
    <Button.Root
      type='button'
      variant='primary'
      mode='stroke'
      size='xsmall'
      onClick={() => navigate(openPanelHref(route, PANEL_PAYOUT, employee.id))}
    >
      <Button.Icon as={RiAddLine} />
      Новое заявление
    </Button.Root>
  ) : (
    <span className='text-paragraph-xs text-text-sub-600'>
      Заявление заводит администратор или финансовый отдел
    </span>
  );

  return (
    <div className='flex flex-col'>
      {/* Всё, что выше бланка, — экран, а не документ: на бумагу идёт только бланк. */}
      <div data-печать='скрыть'>
        <ScreenState
          data={query.data}
          error={query.error ?? null}
          subject='заявления о реквизитах'
          isEmpty={(данные) => данные.items.length === 0}
          onRetry={() => void query.refetch()}
          loading={
            <div className='flex flex-col gap-2 p-5'>
              <Skeleton className='h-16 w-full' />
              <Skeleton className='h-16 w-full' />
            </div>
          }
          empty={
            <EmptyState
              icon={RiBankCardLine}
              title='Реквизитов пока нет'
              description={
                canEdit
                  ? 'Пока заявления нет, в строке ведомости не будет ни карты, ни телефона, и отметить выплату будет нечем.'
                  : 'Заявление заводит администратор или финансовый отдел. Право выдаёт администратор.'
              }
              action={canEdit ? кнопка : undefined}
            />
          }
        >
          {(данные) => {
            const действующее = данные.items.find((з) => з.is_active);
            const история = данные.items.filter((з) => !з.is_active);
            return (
              <div className='flex flex-col gap-4 p-5'>
                <div className='flex items-center justify-between gap-3'>
                  <span className='text-paragraph-sm text-text-sub-600'>
                    {countLabel(данные.items.length, ['заявление', 'заявления', 'заявлений'])} за
                    всё время
                  </span>
                  {кнопка}
                </div>

                {действующее === undefined ? null : (
                  <section className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
                    <div className='mb-2 flex items-center gap-2'>
                      <span className='text-label-sm text-text-strong-950'>Действующее</span>
                      <Badge.Root variant='lighter' color='green' size='medium'>
                        {СПОСОБЫ[действующее.method] ?? действующее.method}
                      </Badge.Root>
                    </div>
                    <FieldList>
                      <FieldRow label='Куда переводить' value={реквизит(действующее)} />
                      <FieldRow
                        label='Банк'
                        value={действующее.bank_name ?? null}
                        missing='банк в заявлении не указан'
                      />
                      {действующее.recipient_name ? (
                        <FieldRow
                          label='Получатель'
                          value={действующее.recipient_name}
                          note='перевод третьему лицу'
                        />
                      ) : null}
                      <FieldRow
                        label='Дата заявления'
                        value={
                          действующее.application_date
                            ? formatDateOnly(действующее.application_date)
                            : null
                        }
                        missing='дата в заявлении не проставлена'
                      />
                      {/* Полные банковские реквизиты есть только у официального бланка (статья 136
                      ТК). У обычного их нет вовсе, и строки «не указано» там были бы враньём о
                      неполноте: бланк заполнен целиком. Поэтому не missing, а отсутствие
                      строки. */}
                      {действующее.bik ? (
                        <FieldRow label='БИК банка' value={действующее.bik} />
                      ) : null}
                      {действующее.corr_account ? (
                        <FieldRow label='Корреспондентский счёт' value={действующее.corr_account} />
                      ) : null}
                      {действующее.settlement_account ? (
                        <FieldRow
                          label='Расчётный счёт'
                          value={действующее.settlement_account}
                          note='официальный бланк'
                        />
                      ) : null}
                    </FieldList>

                    {/* Скан подписанного заявления. Показывается сразу, а не ссылкой «открыть»:
                    смотрят его ровно затем, чтобы убедиться, что подпись человека на месте, и
                    лишнее нажатие тут ничего не экономит. */}
                    <div className='mt-3 flex flex-col gap-2'>
                      <span className='text-label-sm text-text-strong-950'>Скан заявления</span>
                      {действующее.doc_file_id ? (
                        <AttachmentImage
                          url={`/api/v1/files/${действующее.doc_file_id}`}
                          alt={`Заявление о реквизитах, ${employee.display_name}`}
                          что='скан заявления'
                        />
                      ) : (
                        <span className='text-paragraph-sm text-text-sub-600'>
                          Подписанный бланк не приложен. Реквизиты работают, но подтвердить, что
                          человек их подавал, будет нечем.
                        </span>
                      )}
                    </div>
                  </section>
                )}

                <ВыплатыЧеловеку employeeId={employee.id} />

                {история.length === 0 ? null : (
                  <section className='flex flex-col gap-2'>
                    <span className='text-label-sm text-text-strong-950'>
                      История: {countLabel(история.length, ['заявление', 'заявления', 'заявлений'])}
                    </span>
                    <ul className='flex flex-col gap-2'>
                      {история.map((заявление) => (
                        <li
                          key={заявление.id}
                          className='flex items-center justify-between gap-3 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2'
                        >
                          <span className='text-paragraph-sm text-text-sub-600'>
                            {СПОСОБЫ[заявление.method] ?? заявление.method}, {реквизит(заявление)}
                          </span>
                          <span className='text-paragraph-xs text-text-sub-600'>
                            {formatDateOnly(заявление.application_date ?? заявление.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            );
          }}
        </ScreenState>
      </div>

      {/* Бланк стоит ВНЕ состояния списка: он нужен как раз тогда, когда заявления ещё нет —
          кадровик печатает его человеку на подпись при приёме на работу. */}
      <div className='px-5 pb-5'>
        <БланкЗаявления />
      </div>
    </div>
  );
}

/**
 * Отметки о выплатах: когда, сколько и каким способом человеку перевели.
 *
 * Тз, раздел 5.4.6: «Признак виден в реестре ведомостей и в карточке сотрудника». В ведомости он
 * был, в карточке — нет, и на вопрос «а за июль перевели?» приходилось открывать июльскую
 * ведомость и искать в ней строку среди двухсот шестидесяти.
 *
 * Сторно показывается ОТДЕЛЬНОЙ строкой с основанием, а не вычитается молча из суммы: денежная
 * запись у нас не правится нигде, ошиблись — значит сторно, и в истории это обязано быть видно.
 */
function ВыплатыЧеловеку({ employeeId }: { employeeId: string }) {
  const факты = usePayoutFactsQuery(employeeId);

  return (
    <section className='flex flex-col gap-2'>
      <span className='text-label-sm text-text-strong-950'>Выплаты</span>
      <ScreenState
        data={факты.data}
        error={факты.error ?? null}
        subject='отметки о выплатах'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void факты.refetch()}
        loading={<Skeleton className='h-12 w-full' />}
        empty={
          <span className='text-paragraph-sm text-text-sub-600'>
            Отметок о выплатах ещё нет. Они появляются, когда финансовый отдел отмечает выплату по
            ведомости.
          </span>
        }
      >
        {(данные) => (
          <ul className='flex flex-col gap-2'>
            {данные.items.map((факт) => (
              <li
                key={факт.id}
                className='flex items-center justify-between gap-3 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 py-2'
              >
                <div className='flex min-w-0 flex-col gap-0.5'>
                  <span className='text-paragraph-sm text-text-strong-950'>
                    {formatDateOnly(факт.paid_at)}
                    {факт.method_used ? `, ${СПОСОБЫ[факт.method_used] ?? факт.method_used}` : ''}
                    {факт.last4 ? ` · ${факт.last4}` : ''}
                  </span>
                  {факт.reversal_of_id ? (
                    <span className='text-paragraph-xs text-text-sub-600'>
                      Сторно{факт.reversal_basis ? `: ${факт.reversal_basis}` : ''}
                    </span>
                  ) : null}
                </div>
                {/* Знака рубля нет: столбец целиком денежный, единицу назвал заголовок
                    «Выплаты».

                    ⚠️ Минус НЕ дописывается руками. Сторно уходит с сервера уже отрицательной
                    суммой — так требует правило денег («обратная запись», money.md п. 12), — и
                    приписка знака давала «−−33 131,00»: два минуса подряд на живом экране
                    карточки человека. Проверка мимо, потому что её данные клали сторно
                    ПОЛОЖИТЕЛЬНЫМ числом, то есть проверяли то, чего сервер не присылает. */}
                <span className='shrink-0 text-label-sm tabular-nums text-text-strong-950'>
                  {formatKopecks(факт.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </ScreenState>
    </section>
  );
}
